// copy-register: commercial
import { useMemo, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { Glyph } from "@apf/design-system/glyphs";
import type { IntegrationProofClass, IntegrationRecipeDefinition } from "@apf/framework-config";
import {
  businessModulePath,
  domainWorkblockPath,
  downloadAssetPath,
  groundworksEntityPath,
  integrationExamplePath,
  integrationMappingPath,
  integrationRecipePath,
  integrationStandardPath,
  integrationSystemPath,
  integrationToolPath,
  workblockSourceUrl
} from "../catalogue";
import { useCopy, useFramework } from "../framework";
import { EmptyState, PageHeader, SectionHeading, StatusMark, humanize, ordinal } from "../components/ui";
import { CopyAssetButton, copyableFormats, type ManifestAsset } from "../downloadsManifest";
import { assetHref } from "../routerMount";
import { useScrollToHash } from "./support";

interface RecipeFacets {
  domain: string;
  module: string;
  systemRole: string;
  channel: string;
  format: string;
  actionClass: string;
}

type RecipeFacetKey = keyof RecipeFacets;

const initialRecipeFacets: RecipeFacets = {
  domain: "all",
  module: "all",
  systemRole: "all",
  channel: "all",
  format: "all",
  actionClass: "all"
};

interface ConnectionTopic {
  id: string;
  glyph: Parameters<typeof Glyph>[0]["meaning"];
}

const topics: ConnectionTopic[] = [
  { id: "microsoft-365-copilot", glyph: "capability.agent-tools" },
  { id: "sharepoint", glyph: "capability.composer" },
  { id: "sap", glyph: "capability.integration" },
  { id: "systems-of-record", glyph: "class.record" },
  { id: "durable-channels", glyph: "capability.integration" },
  { id: "model-routing", glyph: "capability.data" },
  { id: "tenancy", glyph: "capability.controls" },
  { id: "governance-audit", glyph: "capability.audit" },
  { id: "regulated-delivery", glyph: "capability.controls" },
  { id: "production-promotion", glyph: "foundation.execution-rails" }
];

interface IntegrationPrompt {
  id: string;
  workblockIds?: string[];
  workblockScopeKey?: string;
  recordIds: string[];
}

const prompts: IntegrationPrompt[] = [
  { id: "copilot-tool-surface", workblockScopeKey: "page.connected-work.prompt.copilot-tool-surface.scope", recordIds: ["action-proposal", "decision", "committed-action", "audit-event"] },
  { id: "sharepoint-document-channel", workblockIds: ["contract-lifecycle-management", "supplier-lifecycle-third-party-risk", "sourcing-negotiation"], recordIds: ["contract-version", "evidence-item", "source-record-reference", "audit-event"] },
  { id: "sap-document-bridge", workblockIds: ["buying-ordering-receiving", "invoice-exception-payment-readiness", "supplier-lifecycle-third-party-risk"], recordIds: ["requisition", "purchase-order", "receipt", "invoice", "supplier-master-change"] },
  { id: "system-record-reconciliation", workblockScopeKey: "page.connected-work.prompt.system-record-reconciliation.scope", recordIds: ["source-record-reference", "integration-state", "business-event", "exception-case"] },
  { id: "event-driven-handoff", workblockScopeKey: "page.connected-work.prompt.event-driven-handoff.scope", recordIds: ["business-event", "audit-event", "source-record-reference"] },
  { id: "model-residency-policy", workblockScopeKey: "page.connected-work.prompt.model-residency-policy.scope", recordIds: ["authority-envelope", "evaluation-result", "learning-example", "audit-event"] },
  { id: "human-accountability-bridge", workblockScopeKey: "page.connected-work.prompt.human-accountability-bridge.scope", recordIds: ["authority-envelope", "action-proposal", "decision", "committed-action"] },
  { id: "production-promotion-plan", workblockScopeKey: "page.connected-work.prompt.production-promotion-plan.scope", recordIds: ["registry-release", "integration-state", "evaluation-result", "audit-event"] }
];

const sequence = ["frame", "map", "choose", "govern", "validate", "promote"] as const;

export function replaceTokens(template: string, values: Record<string, string | number>): string {
  return Object.entries(values).reduce((result, [key, value]) => result.replaceAll(`{${key}}`, String(value)), template);
}

export function ProofMark({ proofClass }: { proofClass: IntegrationProofClass }) {
  const copy = useCopy();
  return <span className={`apf-proof-mark apf-proof-mark--${proofClass}`}>{copy(`page.connected-work.proof.${proofClass}`)}</span>;
}

export function InlineLinks({ values, resolve }: { values: string[]; resolve: (value: string) => { label: string; to?: string } }) {
  return <>{values.map((value, index) => { const item = resolve(value); return <span key={value}>{index ? " · " : null}{item.to ? <Link to={item.to}>{item.label}</Link> : item.label}</span>; })}</>;
}

/**
 * Downloadable formats for one governed asset, rendered beside its proof
 * label. Only assets present in the generated site-safe manifest render;
 * nothing is fabricated on the page.
 */
export function AssetFormats({ asset }: { asset: ManifestAsset | undefined }) {
  const copy = useCopy();
  if (!asset) return null;
  return (
    <span className="apf-asset-formats">
      {asset.formats.map((format) => (
        <span key={format.path} className="apf-downloads-row__format">
          <a className="apf-button apf-button--quiet" href={assetHref(format.path)} download>
            {copy("page.downloads.download")} {format.format}
          </a>
          {copyableFormats.has(format.format) ? <CopyAssetButton path={format.path} copy={copy} /> : null}
        </span>
      ))}
      <Link to={downloadAssetPath(asset.assetId)}>{copy("page.downloads.route-label", "Open canonical record")}</Link>
    </span>
  );
}

/** Governed integration recipe catalogue. It reports recipe proof, never live connector state. */
export function ConnectedWorkPage() {
  const { snapshot } = useFramework();
  const copy = useCopy();
  useScrollToHash();
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const [facets, setFacets] = useState<RecipeFacets>(initialRecipeFacets);
  const integrations = snapshot.integrations;
  const recipeById = new Map(integrations.recipes.map((recipe) => [recipe.id, recipe]));
  const domainById = new Map(snapshot.content.domainWorkblocks.map((domain) => [domain.id, domain]));
  const moduleById = new Map(snapshot.businessModuleRegistry.businessModules.map((module) => [module.id, module]));
  const entityById = new Map(snapshot.groundworks.entities.map((entity) => [entity.id, entity]));
  const channelById = new Map(integrations.channels.map((channel) => [channel.id, channel]));
  const formatById = new Map(integrations.formats.map((format) => [format.id, format]));
  const systemRoleById = new Map(integrations.systemRoles.map((role) => [role.id, role]));
  const standardById = new Map(snapshot.groundworks.standards.map((standard) => [standard.id, standard]));
  const agentById = new Map(snapshot.businessModuleRegistry.businessModules.flatMap((module) => module.agents.map((agent) => [agent.id, agent] as const)));
  const legacyRecipeId = searchParams.get("recipe");

  const facetOptions = useMemo(() => ({
    domain: [...new Set(integrations.recipes.flatMap((recipe) => recipe.domainWorkblockIds))].map((id) => ({ id, label: domainById.has(id) ? copy(domainById.get(id)!.nameKey) : humanize(id) })).sort((a, b) => a.label.localeCompare(b.label, "en-GB")),
    module: [...new Set(integrations.recipes.flatMap((recipe) => recipe.businessModuleIds))].map((id) => ({ id, label: moduleById.has(id) ? copy(moduleById.get(id)!.labelKey) : humanize(id) })).sort((a, b) => a.label.localeCompare(b.label, "en-GB")),
    systemRole: [...new Set(integrations.recipes.flatMap((recipe) => recipe.systemRoleIds))].map((id) => ({ id, label: systemRoleById.get(id)?.label ?? humanize(id) })).sort((a, b) => a.label.localeCompare(b.label, "en-GB")),
    channel: [...new Set(integrations.recipes.flatMap((recipe) => recipe.channelIds))].map((id) => ({ id, label: channelById.get(id)?.label ?? humanize(id) })).sort((a, b) => a.label.localeCompare(b.label, "en-GB")),
    format: [...new Set(integrations.recipes.flatMap((recipe) => recipe.formatIds))].map((id) => ({ id, label: formatById.get(id)?.label ?? humanize(id) })).sort((a, b) => a.label.localeCompare(b.label, "en-GB")),
    actionClass: [...new Set(integrations.recipes.flatMap((recipe) => recipe.actionClasses))].map((id) => ({ id, label: copy(`page.connected-work.action-class.${id}`) })).sort((a, b) => a.label.localeCompare(b.label, "en-GB"))
  }), [copy, domainById, integrations.recipes, moduleById]);

  const rows = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase("en-GB");
    return integrations.recipes.filter((recipe) => {
      if (facets.domain !== "all" && !recipe.domainWorkblockIds.includes(facets.domain)) return false;
      if (facets.module !== "all" && !recipe.businessModuleIds.includes(facets.module)) return false;
      if (facets.systemRole !== "all" && !recipe.systemRoleIds.includes(facets.systemRole)) return false;
      if (facets.channel !== "all" && !recipe.channelIds.includes(facets.channel)) return false;
      if (facets.format !== "all" && !recipe.formatIds.includes(facets.format)) return false;
      if (facets.actionClass !== "all" && !recipe.actionClasses.includes(facets.actionClass as IntegrationRecipeDefinition["actionClasses"][number])) return false;
      if (!normalized) return true;
      return [
        recipe.id, recipe.label, recipe.outcome, recipe.trigger, recipe.owner, recipe.direction,
        ...recipe.domainWorkblockIds.flatMap((id) => [id, domainById.has(id) ? copy(domainById.get(id)!.nameKey) : ""]),
        ...recipe.businessModuleIds.flatMap((id) => [id, moduleById.has(id) ? copy(moduleById.get(id)!.labelKey) : ""]),
        ...recipe.channelIds.flatMap((id) => [id, channelById.get(id)?.label ?? ""]),
        ...recipe.formatIds.flatMap((id) => [id, formatById.get(id)?.label ?? ""]),
        ...recipe.systemRoleIds.flatMap((id) => [id, systemRoleById.get(id)?.label ?? ""]),
        ...recipe.actionClasses.map((id) => copy(`page.connected-work.action-class.${id}`))
      ]
        .join(" ").toLocaleLowerCase("en-GB").includes(normalized);
    });
  }, [copy, domainById, facets, integrations.recipes, moduleById, query]);

  /* The retired query-parameter identity resolves to the canonical route. */
  if (legacyRecipeId && recipeById.has(legacyRecipeId)) {
    return <Navigate to={integrationRecipePath(legacyRecipeId)} replace />;
  }

  const copilotExamples = integrations.examples.filter((example) => example.toolRecipeId);
  const crossCuttingExamples = integrations.examples.filter((example) => !example.toolRecipeId);

  return (
    <div className="apf-page apf-register-marketing apf-connected-work-page">
      <PageHeader
        eyebrow={copy("page.connected-work.eyebrow")}
        title={copy("page.connected-work.catalogue-title")}
        description={copy("page.connected-work.catalogue-description")}
        meta={<span>{replaceTokens(copy("page.connected-work.catalogue-meta"), { recipes: integrations.recipes.length, mappings: integrations.moduleBindings.length, systems: integrations.systemProfiles.length })}</span>}
      />

      <aside className="apf-recipe-truth" aria-label={copy("page.connected-work.recipe-boundary-label")}>
        <ProofMark proofClass="recipe" />
        <p>{copy("page.connected-work.recipe-boundary-body")}</p>
      </aside>

      <section className="apf-statement-section apf-statement-section--inverse apf-page-section" aria-labelledby="connected-work-principle">
        <p className="apf-statement-section__eyebrow">{copy("page.connected-work.principle-label")}</p>
        <h2 className="apf-statement-section__headline" id="connected-work-principle">{copy("page.connected-work.principle-title")}</h2>
        <div className="apf-statement-section__column"><p>{copy("page.connected-work.catalogue-principle-body")}</p></div>
      </section>

      <section className="apf-page-section" aria-labelledby="recipe-catalogue-title">
        <SectionHeading id="recipe-catalogue-title" title={copy("page.connected-work.recipe-library-title")} description={copy("page.connected-work.recipe-library-description")} />
        <div className="apf-integration-filter" role="group" aria-label={copy("page.connected-work.recipe-filter-label")}>
          <label className="apf-search-field">
            <span>{copy("page.connected-work.recipe-search-label")}</span>
            <input type="search" placeholder={copy("page.connected-work.recipe-search-placeholder")} value={query} onChange={(event) => setQuery(event.target.value)} />
          </label>
          <div className="apf-integration-filter__facets">
            {(Object.keys(facetOptions) as RecipeFacetKey[]).map((key) => <label key={key}><span>{copy(`page.connected-work.facet.${key}`)}</span><select value={facets[key]} onChange={(event) => setFacets((current) => ({ ...current, [key]: event.target.value }))}><option value="all">{copy(`page.connected-work.facet.${key}.all`)}</option>{facetOptions[key].map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}</select></label>)}
          </div>
          <div className="apf-integration-filter__status"><p aria-live="polite">{replaceTokens(copy("page.connected-work.filter-results"), { count: rows.length, total: integrations.recipes.length })}</p><button type="button" onClick={() => { setQuery(""); setFacets(initialRecipeFacets); }}>{copy("page.connected-work.clear-filters")}</button></div>
        </div>
        {rows.length ? <ol className="apf-recipe-index">{rows.map((recipe) => (
          <li className="apf-recipe-index__row" key={recipe.id}>
            <Link to={integrationRecipePath(recipe.id)}>
              {/* The ordinal is the recipe's place in the governed registry
                * order, so a filtered view keeps stable numbers instead of
                * renumbering from 01. */}
              <span className="apf-recipe-index__ordinal">{ordinal((integrations.recipes.findIndex(({ id }) => id === recipe.id)) + 1)}</span>
              <span className="apf-recipe-index__heading"><Glyph meaning={recipe.id.includes("mcp") || recipe.id.includes("copilot") ? "capability.agent-tools" : "capability.integration"} size={20} /><strong>{recipe.label}</strong><ProofMark proofClass="recipe" /></span>
              <span className="apf-recipe-index__outcome">{recipe.outcome}</span>
              <span className="apf-recipe-index__meta">{replaceTokens(copy("page.connected-work.recipe-index-meta"), { direction: copy(`page.connected-work.recipe-direction.${recipe.direction}`), modules: recipe.businessModuleIds.length, records: recipe.entityIds.length })}</span>
            </Link>
          </li>
        ))}</ol> : <EmptyState title={copy("page.connected-work.recipe-empty-title")} description={copy("page.connected-work.recipe-empty-description")} />}
      </section>

      <section className="apf-page-section apf-band apf-band--tint" aria-labelledby="copilot-architecture-title">
        <SectionHeading id="copilot-architecture-title" title={copy("page.connected-work.copilot-architecture-title")} description={copy("page.connected-work.copilot-architecture-description")} />
        <div className="apf-copilot-relationship" aria-label={copy("page.connected-work.copilot-relationship-label")}>
          {integrations.copilotAccessContract.relationship.split(" → ").map((step, index) => <div key={step}><span>{ordinal(index + 1)}</span><strong>{step}</strong></div>)}
        </div>
        <p className="apf-copilot-authority">{integrations.copilotAccessContract.authorityBoundary}</p>
        <ol className="apf-copilot-capabilities">
          {integrations.copilotAccessContract.capabilities.map((capability, index) => <li key={capability.id}><span>{ordinal(index + 1)}</span><div><h3>{capability.label}</h3><p>{capability.description}</p></div></li>)}
        </ol>
        <div className="apf-copilot-packages">
          {integrations.copilotDomainPackages.map((pkg) => {
            const domain = domainById.get(pkg.domainWorkblockId);
            return <article key={pkg.id}>
              <header><ProofMark proofClass={pkg.proofClass} /><h3>{domain ? copy(domain.nameKey) : pkg.label}</h3></header>
              <p>{replaceTokens(copy("page.connected-work.package-count"), { tools: pkg.toolRecipeIds.length, agents: pkg.agentIds.length })}</p>
              <ul>{pkg.agentIds.map((id) => <li key={id}>{agentById.get(id)?.label ?? humanize(id)}</li>)}</ul>
              {domain ? <Link to={`${domainWorkblockPath(domain.id)}#connections`}>{copy("page.connected-work.package-link")}</Link> : null}
            </article>;
          })}
        </div>
      </section>

      <section className="apf-page-section" aria-labelledby="tool-catalogue-title">
        <SectionHeading id="tool-catalogue-title" title={copy("page.connected-work.tool-catalogue-title")} description={copy("page.connected-work.tool-catalogue-description")} />
        <div className="apf-tool-catalogue">
          {integrations.copilotDomainPackages.map((pkg) => {
            const domain = domainById.get(pkg.domainWorkblockId);
            return <details key={pkg.id}>
              <summary><span><strong>{domain ? copy(domain.nameKey) : pkg.label}</strong><small>{replaceTokens(copy("page.connected-work.tool-package-count"), { count: pkg.toolRecipeIds.length })}</small></span><ProofMark proofClass={pkg.proofClass} /></summary>
              <ol>{pkg.toolRecipeIds.map((toolId) => {
                const tool = integrations.toolRecipes.find((candidate) => candidate.id === toolId);
                if (!tool) return null;
                const module = moduleById.get(tool.businessModuleId);
                return <li data-tool-recipe={tool.id} key={tool.id}>
                  <header><div><h3><Link to={integrationToolPath(tool.id)}>{tool.label}</Link></h3><p>{tool.outcome}</p></div><ProofMark proofClass={tool.proofClass} /></header>
                  <p className="apf-tool-catalogue__meta">{module ? <Link to={businessModulePath(module.domainWorkblockId, module.id)}>{copy(module.labelKey)}</Link> : humanize(tool.businessModuleId)} · {copy(`page.connected-work.action-class.${tool.actionClass}`)}</p>
                  <p><Link to={integrationToolPath(tool.id)}>{copy("page.connected-work.open-record", "Open the canonical record")}</Link></p>
                </li>;
              })}</ol>
            </details>;
          })}
        </div>
      </section>

      <section className="apf-page-section" aria-labelledby="copilot-examples-title">
        <SectionHeading id="copilot-examples-title" title={copy("page.connected-work.copilot-examples-title")} description={copy("page.connected-work.copilot-examples-description")} />
        <ol className="apf-example-list">
          {copilotExamples.map((example, index) => {
            const module = example.businessModuleId ? moduleById.get(example.businessModuleId) : undefined;
            const domain = example.domainWorkblockId ? domainById.get(example.domainWorkblockId) : undefined;
            const tool = example.toolRecipeId ? integrations.toolRecipes.find((candidate) => candidate.id === example.toolRecipeId) : undefined;
            return <li id={`example-${example.id}`} key={example.id}>
              <header><span>{ordinal(index + 1)}</span><div><p>{domain ? copy(domain.nameKey) : copy("page.connected-work.example.default-domain")}</p><h3><Link to={integrationExamplePath(example.id)}>{example.label}</Link></h3></div><ProofMark proofClass={example.proofClass} /></header>
              <p className="apf-example-list__question">{example.userQuestion}</p>
              <p>{example.output}</p>
              <dl><div><dt>{copy("page.connected-work.example.module-label")}</dt><dd>{module ? <Link to={businessModulePath(module.domainWorkblockId, module.id)}>{copy(module.labelKey)}</Link> : copy("page.connected-work.example.cross-domain")}{module ? <> · <StatusMark value={module.implementationStatus} /></> : null}</dd></div><div><dt>{copy("page.connected-work.example.tool-proof-label")}</dt><dd>{tool ? <><ProofMark proofClass={tool.proofClass} /> · {copy(`page.connected-work.action-class.${tool.actionClass}`)}</> : copy("page.connected-work.example.not-applicable")}</dd></div><div><dt>{copy("page.connected-work.example.authority-ceiling-label")}</dt><dd>{example.authorityBoundary}</dd></div></dl>
              {example.workblockPath ? <p><a href={workblockSourceUrl(example.workblockPath)} target="_blank" rel="noreferrer">{copy("page.connected-work.open-demo-workblock")}</a></p> : null}
              {example.demoReadiness ? <p className="apf-demo-readiness"><strong>{example.demoReadiness}</strong></p> : null}
              {example.flowSteps ? <details><summary>{copy("page.connected-work.example-flow-label")}</summary><ol>{example.flowSteps.map((step) => <li key={step}>{step}</li>)}</ol></details> : null}
            </li>;
          })}
        </ol>
      </section>

      <section className="apf-page-section" aria-labelledby="cross-cutting-examples-title">
        <SectionHeading id="cross-cutting-examples-title" title={copy("page.connected-work.cross-cutting-examples-title")} description={copy("page.connected-work.cross-cutting-examples-description")} />
        <ol className="apf-example-list apf-example-list--cross-cutting">
          {crossCuttingExamples.map((example, index) => <li id={`example-${example.id}`} key={example.id}>
            <header><span>{ordinal(index + 1)}</span><div><p>{copy("page.connected-work.example.cross-cutting-label")}</p><h3><Link to={integrationExamplePath(example.id)}>{example.label}</Link></h3></div><ProofMark proofClass={example.proofClass} /></header>
            <p>{example.summary}</p>
            <dl>
              <div><dt>{copy("page.connected-work.example.owner-label")}</dt><dd>{example.owner}</dd></div>
              <div><dt>{copy("page.connected-work.example.authority-boundary-label")}</dt><dd>{example.authorityBoundary}</dd></div>
              <div><dt>{copy("page.connected-work.example.client-configuration-label")}</dt><dd>{example.nextAction}</dd></div>
              <div><dt>{copy("page.connected-work.example.recipes-label")}</dt><dd><InlineLinks values={example.recipeIds} resolve={(id) => ({ label: recipeById.get(id)?.label ?? humanize(id), to: integrationRecipePath(id) })} /></dd></div>
              <div><dt>{copy("page.connected-work.example.records-label")}</dt><dd><InlineLinks values={example.entityIds} resolve={(id) => ({ label: entityById.get(id)?.label ?? humanize(id), to: entityById.has(id) ? groundworksEntityPath(id) : undefined })} /></dd></div>
            </dl>
          </li>)}
        </ol>
      </section>

      <section className="apf-page-section" aria-labelledby="mapping-profiles-title">
        <SectionHeading id="mapping-profiles-title" title={copy("page.connected-work.mapping-title")} description={copy("page.connected-work.mapping-description")} />
        <div className="apf-mapping-list">{integrations.mappingProfiles.map((profile) => <article key={profile.id}><header><h3><Link to={integrationMappingPath(profile.id)}>{profile.label}</Link></h3><span>{copy("page.connected-work.client-schema-label")}</span></header><p>{profile.mappingBoundary}</p><p><Link to={integrationMappingPath(profile.id)}>{copy("page.connected-work.open-record", "Open the canonical record")}</Link></p></article>)}</div>
      </section>

      <section className="apf-page-section" aria-labelledby="standard-profiles-title">
        <SectionHeading id="standard-profiles-title" title={copy("page.connected-work.standards-title")} description={copy("page.connected-work.standards-description")} />
        <div className="apf-standard-profile-list">{integrations.formats.map((format) => {
          const standard = format.standardId ? standardById.get(format.standardId) : undefined;
          return <details key={format.id}><summary><span><strong>{format.label}</strong><small>{format.role}</small></span><span>{standard ? standard.version : copy("page.connected-work.client-pinned-format")}</span></summary><p>{format.pinning}</p><p><Link to={integrationStandardPath(format.id)}>{copy("page.connected-work.open-record", "Open the canonical record")}</Link></p></details>;
        })}</div>
      </section>

      <section className="apf-page-section" aria-labelledby="system-landscape-title">
        <SectionHeading id="system-landscape-title" title={copy("page.connected-work.systems-title")} description={copy("page.connected-work.systems-description")} />
        <div className="apf-system-family-list">{integrations.systemFamilies.map((family) => {
          const systems = integrations.systemProfiles.filter((system) => system.familyId === family.id);
          return <details key={family.id}><summary><span><strong>{family.label}</strong><small>{family.responsibility}</small></span><span>{replaceTokens(copy("page.connected-work.system-count"), { systems: systems.length })}</span></summary><ul>{systems.map((system) => <li key={system.id}><h3><Link to={integrationSystemPath(system.id)}>{system.name}</Link></h3><p>{system.recipeIds.length ? <InlineLinks values={system.recipeIds} resolve={(id) => ({ label: recipeById.get(id)?.label ?? humanize(id), to: integrationRecipePath(id) })} /> : copy("page.connected-work.system.discovery-required")}</p></li>)}</ul></details>;
        })}</div>
      </section>

      <section className="apf-page-section" aria-labelledby="proof-legend-title">
        <SectionHeading id="proof-legend-title" title={copy("page.connected-work.proof-title")} description={copy("page.connected-work.proof-description")} />
        <dl className="apf-proof-legend">{integrations.proofLegend.map((entry) => <div key={entry.id}><dt><ProofMark proofClass={entry.id} /></dt><dd>{entry.definition}</dd></div>)}</dl>
      </section>

      <section className="apf-page-section" aria-labelledby="connected-work-topics">
        <SectionHeading id="connected-work-topics" title={copy("page.connected-work.topics-title")} description={copy("page.connected-work.topics-description")} />
        <div className="apf-connection-topic-list">
          {topics.map((topic, index) => <article className="apf-connection-topic" id={topic.id} key={topic.id}>
            <div className="apf-connection-topic__heading"><span>{ordinal(index + 1)}</span><Glyph meaning={topic.glyph} size={20} /><h3>{copy(`page.connected-work.topic.${topic.id}.title`)}</h3></div>
            <div className="apf-connection-topic__content"><p>{copy(`page.connected-work.topic.${topic.id}.position`)}</p><div className="apf-connection-topic__question"><strong>{copy("page.connected-work.resolve-label")}</strong><p>{copy(`page.connected-work.topic.${topic.id}.question`)}</p></div></div>
          </article>)}
        </div>
      </section>

      <section className="apf-page-section" aria-labelledby="connected-work-prompts">
        <SectionHeading id="connected-work-prompts" title={copy("page.connected-work.prompts-title")} description={copy("page.connected-work.prompts-description")} />
        <ol className="apf-integration-prompt-list">
          {prompts.map((item, index) => <li className="apf-integration-prompt" id={item.id} key={item.id}>
            <header><span>{ordinal(index + 1)}</span><h3>{copy(`page.connected-work.prompt.${item.id}.title`)}</h3></header>
            <div className="apf-integration-prompt__bindings">
              <div><strong>{copy("page.connected-work.workblock-scope-label")}</strong><p className="apf-inline-link-list">{item.workblockScopeKey ? copy(item.workblockScopeKey) : item.workblockIds?.map((id, itemIndex) => { const workblock = domainById.get(id); return <span key={id}>{itemIndex ? " / " : null}{workblock ? <Link to={domainWorkblockPath(id)}>{copy(workblock.labelKey)}</Link> : id}</span>; })}</p></div>
              <div><strong>{copy("page.connected-work.records-label")}</strong><p className="apf-inline-link-list">{item.recordIds.map((id, itemIndex) => { const entity = entityById.get(id); return <span key={id}>{itemIndex ? " / " : null}{entity ? <Link to={groundworksEntityPath(id)}>{entity.label}</Link> : id}</span>; })}</p></div>
            </div>
            <div className="apf-integration-prompt__copy"><strong>{copy("page.connected-work.tailoring-prompt-label")}</strong><p>{copy(`page.connected-work.prompt.${item.id}.body`)}</p></div>
          </li>)}
        </ol>
      </section>

      <section className="apf-page-section" aria-labelledby="connected-work-sequence">
        <SectionHeading id="connected-work-sequence" title={copy("page.connected-work.sequence-title")} description={copy("page.connected-work.sequence-description")} />
        <ol className="apf-connection-sequence">{sequence.map((step, index) => <li key={step}><span>{ordinal(index + 1)}</span><div><h3>{copy(`page.connected-work.sequence.${step}.title`)}</h3><p>{copy(`page.connected-work.sequence.${step}.body`)}</p></div></li>)}</ol>
      </section>

      <section className="apf-page-section apf-enterprise-close" aria-labelledby="connected-work-close">
        <SectionHeading id="connected-work-close" title={copy("page.connected-work.close-title")} />
        <p>{copy("page.connected-work.close-body")}</p>
        <Link to="/groundworks">{copy("page.connected-work.data-model-link")}</Link>
        {(snapshot.content.editorialStories ?? []).map((story) => (
          <Link key={story.id} to={`/client-engagement/${story.id}`}>
            {`${copy("page.connected-work.editorial-story-link", "Read the cross-domain story")}: ${copy(story.titleKey)}`}
          </Link>
        ))}
      </section>
    </div>
  );
}
